<template>
  <screen
    :screen-info="headText"
    :can-skip="false"
    :can-leave="!connecting && !scanningStoreQr"
    @leave="cancel"
    no-padding
    scrollable
  >
    <!-- Scanning Area -->
    <div v-if="!scanDone" class="scan-area-outer relative">
      <div class="scan-area-inner animate"></div>
      <div class="scan-area-inner icon-container">
        <img class="absolute centered" src="../assets/Discovery/bolt.svg" alt="Scanning" />
        <div
          v-for="(device, idx) in redactedList"
          class="absolute"
          :key="idx"
          :style="getStyle(idx)"
        >
          <img
            v-if="device.type === 'foot'"
            src="../assets/Discovery/foot_scanner_icon.svg"
            :alt="device.name"
          />
          <img v-else src="../assets/Discovery/body_scanner_icon.svg" :alt="device.name" />
        </div>
      </div>
    </div>
    <!-- Device List -->
    <div v-else class="w-full">
      <!-- Device Card -->
      <slider @mounted="initSlider">
        <slide v-for="(device, idx) in deviceList" :key="idx">
          <scanner-card :scanner="device" @click="connectToDevice(idx)" />
        </slide>
      </slider>
      <c-button class="mt-10 mx-auto" @click="connectToDevice(sliderIndex)">
        <span>{{ $t('discovery.start_scan') }}</span>
      </c-button>
    </div>
    <!-- Prompt -->
    <rounded-card v-if="showPrompt" show-right-btn class="p-3" @set-close-status="cancel">
      <div class="px-6 p-y pt-0 text-center flex flex-1 flex-col items-center">
        <!-- Enable BT -->
        <template v-if="!isBluetoothAvailable">
          <h3 class="text-xl">
            {{ $t('discovery.turn_on_bt_hint') }}
          </h3>
          <p v-if="isIOS" class="text-m bt-hint" v-html="$t('discovery.turn_on_bt_hint_ios')"></p>
          <p v-else class="text-m bt-hint" v-html="$t('discovery.turn_on_bt_hint_android')"></p>
          <img
            class="my-auto py-10"
            src="../assets/Discovery/enable_bt.svg"
            alt="Enable Bluetooth"
          />
        </template>
        <!-- No Scanners -->
        <template v-else>
          <h3 class="text-xl">
            {{ $t('discovery.no_scanners') }}
          </h3>
          <c-button class="my-auto" @click="startDeviceScan">
            <span>{{ $t('discovery.try_again') }}</span>
          </c-button>
        </template>
      </div>
    </rounded-card>
    <rounded-card
      v-if="showForceReleasePrompt"
      :head-text="$t('discovery.scanner_is_busy_title')"
      show-right-btn
      class="p-3"
      @set-close-status="closeForceReleasePrompt"
    >
      <div class="px-6 p-y pt-0 text-center flex flex-1 flex-col items-center">
        <h3 class="text-m mb-5">
          {{ $t('discovery.scanner_is_busy_desc') }}
        </h3>
        <div>
          <c-button class="my-5" @click="forceRelease">
            <span>{{ $t('discovery.scanner_is_busy_force_release') }}</span>
          </c-button>
          <c-button secondary class="my-5" @click="cancel">
            <span>{{ $t('discovery.scanner_is_busy_wait') }}</span>
          </c-button>
        </div>
      </div>
    </rounded-card>
    <unsupported-device v-if="showMobileScanIncapable" @close="showMobileScanIncapable = false" />
    <!-- Doubles as the store QR scanner and the connecting progress overlay -->
    <qr-scan
      v-show="showQrOverlay"
      local-only
      :steps="2"
      :step="qrOverlayStep"
      :message="qrOverlayMessage"
      :show-camera="scanningStoreQr"
      :can-leave="scanningStoreQr"
      :invalid="qrCodeInvalid"
      @decode="onStoreQrDecode"
      @done="done"
      @cancel="onQrOverlayCancel"
      @dismiss-invalid="qrCodeInvalid = false"
    />
  </screen>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import op from 'object-path';
import ApiActions from '@/modules/api-actions';
import { EVENTS as HW_EVENTS } from '@/events/hardware';
import page from '@/mixins/page';
import audioPlayer from '@/mixins/audio-player';
import hasSlider from '@/mixins/has-slider';
import scannerError from '@/mixins/scanner-error';
import ENVS from '@/modules/envs';
import ScanProcess from '@/modules/scan-process';
import WebBT from '@/modules/web-bluetooth'; // eslint-disable-line
import { wait } from '@/modules/utils';
import { parseStoreScanFromText } from '@/modules/qr-action';
import { SET_SCANNER_ID } from '@/store/modules/hardware';
import { TG3D_MOBILE_SCAN_PROVIDER } from '@/store/modules/user';
import i18n from '@/i18n';
import Screen from '@/components/common/Screen.vue';
import QrScan from '@/components/Welcome/QrScan.vue';
import RoundedCard from '@/components/common/RoundedCard.vue';
import UnsupportedDevice from '@/components/common/UnsupportedDevice.vue';
import CButton from '@/components/common/CButton.vue';
import ScannerCard from '@/components/common/ScannerCard.vue';
import Slider from '@/components/common/Slider.vue';
import Slide from '@/components/common/Slide.vue';

const isFoot = d => {
  const { type } = d;
  return !!parseInt(type, 10);
};
const isMobileScan = d => d.type === 'mobile';

const MOCK_SCANNERS = [
  { id: 'BodyScanner-Mock', name: 'BodyScanner-Mock', type: 'body' },
  { id: 'FootScanner-Mock', name: 'FootScanner-Mock', type: 'foot' },
];

// The mobile scan card is shown before we know which store it belongs to
const MOBILE_CARD_NAME = 'iPhone';

export default {
  name: 'Discovery',
  mixins: [page, audioPlayer, hasSlider, scannerError],
  components: {
    Screen,
    RoundedCard,
    CButton,
    ScannerCard,
    QrScan,
    Slider,
    Slide,
    UnsupportedDevice,
  },
  data() {
    return {
      headText: i18n.t('discovery.searching'),
      showPrompt: false,
      showForceReleasePrompt: false,
      scanDone: false,
      deviceList: [],
      connecting: false,
      step: 0,
      connectionHint: '',
      footScannerConnected: false,
      showMobileScanIncapable: false,
      armsRangeSet: false,
      scanningStoreQr: false,
      qrCodeInvalid: false,
      // Arrived from a store QR code, so this page is only a staging post on the
      // way to the mobile scan and none of its own UI should ever be seen. Read
      // from the route here rather than in mounted: the isBluetoothAvailable
      // watcher is immediate and would otherwise start a device scan first.
      startingFromQr: !!this.$route.params.storeId,
      // store settings of the QR code scanned in this session, credentials
      // included. Never persisted, dropped when leaving the page.
      storeConfigs: null,
      // one time token from the QR code, mandatory for starting a mobile scan.
      // Lives exactly as long as storeConfigs.
      storeQrToken: '',
    };
  },
  mounted() {
    if (this.hasFixedScanner) {
      // the fixed scanner wins over whatever was scanned, so this is a plain
      // Discovery run again
      this.startingFromQr = false;
      this.useFixedScanner();
      // Initialize BT module
      if (!WebBT.enabled) {
        this.$events.emit(HW_EVENTS.START_BT_SCAN, true);
      }
      return;
    }
    this.$events.on(HW_EVENTS.BT_DEVICE_CHANGED, this.updateDeviceList);
    this.$store.commit(`hardware/${SET_SCANNER_ID}`, '');
    // Arrived from a store QR code scanned elsewhere (home page, deep link).
    // The device scan is deliberately skipped: its 3s timer would flip scanDone
    // mid flight and flash the scanner picker -- plus its "select a scanner"
    // voice line -- over a choice the user already made by scanning.
    // recoverFromQrEntry puts it back if the mobile scan can't start.
    const { storeId, qrToken } = this.$route.params;
    if (storeId) {
      this.startMobileScanForStore(storeId, qrToken);
      return;
    }
    this.startDeviceScan();
    this.playAudio('ms-01-welcome', true);
  },
  beforeDestroy() {
    this.$events.off(HW_EVENTS.BT_DEVICE_CHANGED, this.updateDeviceList);
    this.storeConfigs = null;
    this.storeQrToken = '';
  },
  methods: {
    ...mapActions('hardware', ['toggleArmDetection', 'setArmsRange']),
    ...mapActions('app', ['setStoreConfigs']),
    startDeviceScan() {
      this.headText = this.$t('discovery.searching');
      this.deviceList = [];
      if (ENVS.mock) {
        this.deviceList = [...MOCK_SCANNERS];
      }
      this.sliderIndex = 0;
      this.scanDone = false;
      this.closePrompt();
      this.$events.emit(HW_EVENTS.START_BT_SCAN, true);
      wait(3).then(() => {
        this.addMobileScan();
        this.scanDone = true;
      });
    },
    useFixedScanner() {
      this.sliderIndex = 0;
      this.deviceList.unshift({
        name: this.fixedScannerId,
        type: this.fixedScannerType || 'body',
        id: this.fixedScannerId,
        key: this.fixedSessionKey,
      });
      this.scanDone = true;
      this.connectToDevice(0);
    },
    addMobileScan() {
      if (this.mobileScanInList) return;

      // No credentials yet: they come from the store QR code scanned on tap
      this.deviceList.unshift({ name: MOBILE_CARD_NAME, type: 'mobile' });
      if (this.slideCount > 1) {
        this.sliderIndex = this.sliderIndex > 0 ? this.sliderIndex : 1;
      }
    },
    waitForArmRangesSet(timeout) {
      if (this.armsRangeSet || !this.connecting) {
        return Promise.resolve();
      }
      if (timeout === 0) {
        return Promise.reject(new Error('[BT]CONNECTION_TIMEOUT'));
      }
      return wait(1).then(() => this.waitForArmRangesSet(timeout - 1));
    },
    handleArmDetection({ data }) {
      if (!Array.isArray(data) || data.length < 2) {
        console.log('handleArmDetection: corrupted data', data);
        return;
      }

      if (data.length === 4) {
        this.setArmsRange(data);
        this.armsRangeSet = true;
        this.$events.off(HW_EVENTS.BT_DATA, this.handleArmDetection);
      }
    },
    async connectToDevice(index) {
      const device = this.deviceList[this.sliderIndex];
      if (!device || index !== this.sliderIndex) return;

      if (isMobileScan(device)) {
        // Already scanned a store QR in this session (e.g. an earlier attempt
        // was cancelled), no need to make the user scan it again
        if (this.storeConfigs) await this.startMobileScan();
        else this.openStoreQrScan();
        return;
      }
      await this.connectToBTDevice(device);
    },
    async connectToBTDevice(device) {
      console.log('connecting to device', device);
      try {
        this.connecting = true;
        this.step = 1;
        this.connectionHint = i18n.t('welcome.scan_code_connect_to_scanner');
        if (WebBT.enabled && !ENVS.mock) {
          await WebBT.requestDevice(device.id).catch(e =>
            Promise.reject(new Error(`[BT]${e.message}`))
          );
        }
        console.log('initializing scanner', device.id, device.key);
        const { provider, id, key, type, token } = device;
        await ScanProcess.cancel();
        await ScanProcess.initial({ provider, id, key, type, token }, this.chargeOnUser);
        this.$store.commit(`hardware/${SET_SCANNER_ID}`, id);
        if (type === 'foot') {
          this.footScannerConnected = true;
          this.playAudio('foot_begin', true);
        } else {
          if (!ScanProcess.withHandles() && !ENVS.mock) {
            this.toggleArmDetection(true);
            this.$events.on(HW_EVENTS.BT_DATA, this.handleArmDetection);
            this.$events.emit(HW_EVENTS.START_ARM_DETECTION, id);
            await this.waitForArmRangesSet(15);
          }
          this.playAudio('4-Ready_to_go', true);
        }
        this.step = 2;
        this.connectionHint = i18n.t('welcome.scan_code_done');
        ScanProcess.reportEvent('ble_connected');
      } catch (e) {
        this.handleScanError(e, device);
      }
    },
    /**
     * The mobile scan a store QR asked for didn't start, so the user is stuck on
     * a Discovery that skipped its own setup. Turn it back into a normal one --
     * device list included, which is the only thing left to offer them here.
     *
     * Not behind the invalid-QR card though: its only button goes home, so the
     * list would never be reached, and the device scan would announce "select a
     * scanner" over a card that offers no such choice.
     */
    recoverFromQrEntry() {
      if (!this.startingFromQr || this.qrCodeInvalid) return;

      this.startingFromQr = false;
      this.startDeviceScan();
    },
    openStoreQrScan() {
      this.qrCodeInvalid = false;
      this.scanningStoreQr = true;
    },
    closeStoreQrScan() {
      this.scanningStoreQr = false;
    },
    onQrOverlayCancel() {
      if (this.scanningStoreQr) {
        this.closeStoreQrScan();
        return;
      }
      this.reset();
    },
    async onStoreQrDecode(text) {
      if (!this.scanningStoreQr) return;

      // Close the camera right away: it stops the scanning loop (which would
      // otherwise ignore a rescan of the same code), frees the device for the
      // mobile scan pages, and guards against a second decode landing here
      this.closeStoreQrScan();

      const scan = parseStoreScanFromText(text);
      if (!scan) {
        this.qrCodeInvalid = true;
        return;
      }
      await this.startMobileScanForStore(scan.storeId, scan.qrToken);
    },
    async startMobileScanForStore(storeId, qrToken = '') {
      // No token, no scan. parseStoreScan already rejects the older codes, this
      // catches whoever reaches here another way -- the retry after a failed
      // scan, a hand written route -- before the scanner API is touched.
      if (!qrToken) {
        this.qrCodeInvalid = true;
        return;
      }
      // hide the UI, not just dim it: the loading page is only 75% opaque, and
      // this page's own contents showing through is exactly the flash a QR entry
      // is supposed to avoid
      this.$events.startLoading(true);
      let configs;
      try {
        configs = await ApiActions.mtm.getStoreConfigs(storeId);
      } catch (e) {
        console.log(`[ERROR] Discovery getStoreConfigs error: ${e.message}`);
        this.$events.stopLoading();
        this.qrCodeInvalid = true;
        return;
      }
      // the QR's own token stands in for the session key, so a store without one
      // is still perfectly usable
      if (!configs.mobile_scanner_id) {
        console.log('store has no mobile scanner configured', storeId);
        this.$events.stopLoading();
        this.qrCodeInvalid = true;
        return;
      }
      this.storeConfigs = { ...configs, store_id: storeId };
      // kept next to the configs, not inside them: it is a credential of the
      // scanned QR, not a store setting other pages should ever see
      this.storeQrToken = qrToken;
      await this.startMobileScan();
    },
    async startMobileScan() {
      const configs = this.storeConfigs;
      // remote config may hand back the provider as a string
      const provider = Number(configs.provider) || 0;
      const chargeOnUser = !!configs.charge_on_user;
      // no-op when startMobileScanForStore already opened it
      this.$events.startLoading(true);
      try {
        // memory only, for the later pages of the scan to read
        this.setStoreConfigs(configs);
        if (provider !== TG3D_MOBILE_SCAN_PROVIDER && !this.isMobileScanCapable) {
          this.$events.stopLoading();
          this.showMobileScanIncapable = true;
          this.playAudio('ms-03-unsupported', true);
          this.recoverFromQrEntry();
          return;
        }
        const id = configs.mobile_scanner_id;
        await ScanProcess.cancel();
        await ScanProcess.initial(
          {
            provider,
            id,
            key: configs.mobile_scanner_key,
            // stays valid until a scan produces a body, so retrying the flow
            // with the same QR works as is
            qrToken: this.storeQrToken,
            token: configs.mobile_scanner_token,
            type: 'mobile',
          },
          chargeOnUser
        );
        this.$store.commit(`hardware/${SET_SCANNER_ID}`, id);
        this.$events.stopLoading();
        this.$events.goto(
          provider === TG3D_MOBILE_SCAN_PROVIDER
            ? 'MobileScan2/UserProfile'
            : 'MobileScan/UserHeight'
        );
      } catch (e) {
        this.$events.stopLoading();
        this.handleScanError(e, { type: 'mobile' });
      }
    },
    handleScanError(e, device) {
      if (e.message.indexOf('[BT]') > -1) {
        ScanProcess.reportEvent('ble_error');
        console.log(`[ERROR] Discovery BT error: ${e.message}`);
        this.$events.alert(this.$t('generic.bluetooth_error'));
      } else {
        const { errno, msg } = op.get(e, 'response.data.error', {});
        // force release needs a session key over Bluetooth, which a mobile
        // scanner has no way to provide
        if (errno === 40303 && !isMobileScan(device)) {
          this.showForceReleasePrompt = true;
        } else if (errno === 40308 && isMobileScan(device)) {
          // the QR token is spent or doesn't belong to this scanner, and the
          // store has to put a fresh QR on screen. Drop what we scanned so
          // tapping the card asks for a new code instead of replaying this one.
          this.storeConfigs = null;
          this.storeQrToken = '';
          this.qrCodeInvalid = true;
        } else {
          console.log(`[ERROR] Discovery initializeScan error: ${errno}, ${msg || e.message}`);
          const errorMessage = `${this.getScannerErrorHint(errno, device.type)}${
            errno ? `<br>(${errno})` : ''
          }`;
          this.$events.alert(errorMessage);
        }
      }
      // an alerted init error leaves the user sitting on this page, so it needs
      // the device list back. The 40308 branch above doesn't -- it raises the
      // invalid-QR card, which recoverFromQrEntry declines to recover behind.
      this.recoverFromQrEntry();
      this.reset();
    },
    done() {
      if (!this.connecting) return;

      if (this.footScannerConnected) {
        this.$events.goto('FootScan/ThingsToNote');
      } else {
        this.$events.goto('ThingsToNote');
      }
    },
    reset() {
      this.step = 0;
      this.toggleArmDetection(false);
      if (ScanProcess.isOngoing()) {
        if (!ScanProcess.withHandles()) {
          this.$events.emit(HW_EVENTS.STOP_ARM_DETECTION, ScanProcess.scannerId);
        }
        ScanProcess.cancel().then(() => {
          this.connecting = false;
        });
      } else {
        this.connecting = false;
      }
    },
    updateDeviceList(devices) {
      if (ENVS.mock) return;

      this.deviceList = devices.map(d => ({ ...d, type: isFoot(d) ? 'foot' : 'body' }));
      this.addMobileScan();
      console.log('updateDeviceList', this.deviceList);
    },
    showBTSettings() {
      this.$events.emit(HW_EVENTS.SHOW_BT_SETTINGS);
    },
    async forceRelease() {
      const device = this.deviceList[this.sliderIndex];
      if (!device) return;

      console.log('forceRelease scanner', device.id);
      this.$events.startLoading();
      try {
        await ScanProcess.forceRelease(device.id);
        this.closeForceReleasePrompt();
        this.connectToDevice(this.sliderIndex);
      } catch (e) {
        console.log(e.message);
        const status = op.get(e, 'response.status', 0);
        const errno = op.get(e, 'response.data.error.errno', 0) || status;
        const errorMessage = `${this.$t('welcome.unexpected_error')}${
          errno ? `<br>(${errno})` : ''
        }`;
        this.$events.alert(errorMessage);
      } finally {
        this.$events.stopLoading();
      }
    },
    closePrompt() {
      this.showPrompt = false;
    },
    closeForceReleasePrompt() {
      this.showForceReleasePrompt = false;
    },
    cancel() {
      this.$events.emit(HW_EVENTS.STOP_BT_SCAN);
      if (this.showPrompt && !this.isBluetoothAvailable && this.mobileScanInList) {
        this.closePrompt();
        return;
      }
      this.closePrompt();
      console.log('cancel discovery');
      this.$events.goto(this.isContactless ? 'Welcome' : 'HomePage');
    },
    getStyle(index) {
      /** to show the devices on scanning area like this:
       0 2
       3 1
       * */
      const offset = '15%';
      const atTop = index % 2 === 0;
      const atLeft = index === 0 || index === 3;
      return {
        [atTop ? 'top' : 'bottom']: offset,
        [atLeft ? 'left' : 'right']: offset,
      };
    },
  },
  computed: {
    ...mapState('mtmUser', ['fixedScannerId', 'fixedScannerType', 'fixedSessionKey']),
    ...mapGetters('hardware', ['isBluetoothAvailable', 'isMobileScanCapable']),
    ...mapGetters('app', ['chargeOnUser', 'isWeb']),
    ...mapGetters('mtmUser', ['isContactless', 'hasFixedScanner']),
    showQrOverlay() {
      // qrCodeInvalid keeps it up after the camera closes, so the error card
      // has something to render in
      return this.connecting || this.scanningStoreQr || this.qrCodeInvalid;
    },
    qrOverlayStep() {
      // keep the progress ring idle while scanning so it can't reach `steps`
      // and fire @done
      return this.scanningStoreQr ? 0 : this.step;
    },
    qrOverlayMessage() {
      return this.scanningStoreQr ? this.$t('discovery.scan_store_qr_hint') : this.connectionHint;
    },
    redactedList() {
      return this.deviceList.slice(0, 4);
    },
    slideCount() {
      return this.deviceList.length;
    },
    mobileScanInList() {
      return !!this.deviceList.find(isMobileScan);
    },
  },
  watch: {
    isBluetoothAvailable: {
      handler(nv) {
        // this handler is immediate, so it lands before mounted: without the QR
        // guard it would start the device scan the QR entry exists to skip
        if (this.hasFixedScanner || this.startingFromQr) return;

        this.showPrompt = !nv && !this.isWeb;
        if (nv) {
          this.startDeviceScan();
        }
      },
      immediate: true,
    },
    slideCount(nv) {
      if (this.hasFixedScanner) return;

      const newLastIndex = nv - 1;
      this.sliderIndex = Math.min(newLastIndex, this.sliderIndex);
      this.resetSlider();
      // The mobile scan card is always listed, so an empty list is no longer a
      // failure state: only a disabled Bluetooth radio is worth prompting for
      this.showPrompt = !this.isBluetoothAvailable && !this.isWeb;
    },
    scanDone(nv, ov) {
      if (!ov && nv && this.slideCount > 0) {
        this.playAudio('ms-02-select_nearby', true);
        this.headText = this.$t('discovery.select_scanner');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.scan-area-outer {
  width: 80%;
  max-width: 500px;
  background-image: linear-gradient(to right, #540deb, #2245ff);
  border-radius: 100%;
  position: relative;
  overflow: visible;
  &:after {
    content: '';
    display: block;
    padding-bottom: 100%;
  }
}
.scan-area-inner {
  @apply w-full h-full rounded-full;
  &.animate {
    animation: scanning 3s backwards infinite;
    opacity: 0.1;
    background-color: #f5f5f5;
  }
}
.icon-container {
  @apply absolute top-0 left-0;
  padding: 15%;
}
.force-width {
  max-width: 300px !important;
  min-width: 300px !important;
}
@keyframes scanning {
  0% {
    transform: scale(0.1);
  }
  100% {
    transform: scale(1);
  }
}
.bt-hint {
  text-align: left;
  margin: 1rem 1rem 0rem 1rem;
  line-height: 2rem;
}
</style>
